import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';

import {
  ALL_SECTIONS_KEY,
  ALL_SECTIONS_LABEL,
  ChangeKindFilter,
  SECTION_PREFIX,
  SUBGROUP_PREFIX,
} from '../../PendingChangesDrawer.constants';

import PendingChangesSidebarCounts from './components/PendingChangesSidebarCounts';
import {
  expandedIconStyles,
  expandIconStyles,
  getItemStyles,
  getLabelStyles,
  getLabelVariant,
  getSubItemStyles,
  getSubLabelStyles,
  getSubLabelVariant,
  sectionHeaderRowStyles,
  sidebarStyles,
} from './PendingChangesSidebar.styles';
import {
  getDefaultExpandedSections,
  getTotalCount,
  getVisibleKinds,
} from './PendingChangesSidebar.utils';

const allKinds = Object.values(ChangeKindFilter);

const countsPropType = PropTypes.shape({
  [ChangeKindFilter.DIRECT]: PropTypes.number.isRequired,
  [ChangeKindFilter.RECALCULATED]: PropTypes.number.isRequired,
});

const PendingChangesSidebar = ({
  sections,
  selectedSection,
  selectedKind,
  totalCounts,
  onSectionSelect,
  onKindSelect,
}) => {
  const visibleKinds = getVisibleKinds(selectedKind);

  const defaultExpanded = useMemo(() => getDefaultExpandedSections(sections), [sections]);

  const [expandedSections, setExpandedSections] = useState({});

  const handleSectionClick = useCallback(
    event => {
      const { section } = event.currentTarget.dataset;

      onSectionSelect(section);
    },
    [onSectionSelect],
  );

  const handleExpandToggle = useCallback(event => {
    event.stopPropagation();
    const { section } = event.currentTarget.dataset;

    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  useEffect(() => {
    setExpandedSections(prev => ({ ...defaultExpanded, ...prev }));
  }, [defaultExpanded]);

  return (
    <Box sx={sidebarStyles}>
      <Box
        data-section={ALL_SECTIONS_KEY}
        sx={getItemStyles(selectedSection, ALL_SECTIONS_KEY)}
        onClick={handleSectionClick}
      >
        <Typography
          noWrap
          sx={getLabelStyles(selectedSection, ALL_SECTIONS_KEY, getTotalCount(totalCounts))}
          variant={getLabelVariant(selectedSection, ALL_SECTIONS_KEY)}
        >
          {ALL_SECTIONS_LABEL}
        </Typography>
        <PendingChangesSidebarCounts
          activeKind={selectedKind}
          counts={totalCounts}
          isRowSelected={selectedSection === ALL_SECTIONS_KEY}
          kinds={allKinds}
          onCountSelect={onKindSelect}
        />
      </Box>

      {sections.map(section => {
        const sectionKey = `${SECTION_PREFIX}${section.name}`;
        const isExpanded = expandedSections[section.name];

        return (
          <React.Fragment key={sectionKey}>
            <Box
              data-section={sectionKey}
              sx={getItemStyles(selectedSection, sectionKey)}
              onClick={handleSectionClick}
            >
              <Box sx={sectionHeaderRowStyles}>
                <Typography
                  noWrap
                  sx={getLabelStyles(selectedSection, sectionKey, getTotalCount(section.counts))}
                  title={section.name}
                  variant={getLabelVariant(selectedSection, sectionKey)}
                >
                  {section.name}
                </Typography>
                {!!section.hasSubGroups && (
                  <ArrowCheckIcon
                    data-section={section.name}
                    direction={IconDirection.DOWN}
                    size={IconSize[14]}
                    style={isExpanded ? expandedIconStyles : expandIconStyles}
                    onClick={handleExpandToggle}
                  />
                )}
              </Box>
              <PendingChangesSidebarCounts
                activeKind={selectedSection === sectionKey ? selectedKind : null}
                counts={section.counts}
                isRowSelected={selectedSection === sectionKey}
                kinds={visibleKinds}
              />
            </Box>

            {!!isExpanded &&
              !!section.subGroups.length &&
              section.subGroups.map(subGroup => {
                const subGroupName = _.isNil(subGroup.name) ? 'Other' : subGroup.name;
                const subGroupKey = `${SUBGROUP_PREFIX}${section.name}:${subGroupName}`;

                return (
                  <Box
                    data-section={subGroupKey}
                    key={subGroupKey}
                    sx={getSubItemStyles(selectedSection, subGroupKey)}
                    onClick={handleSectionClick}
                  >
                    <Typography
                      noWrap
                      sx={getSubLabelStyles(
                        selectedSection,
                        subGroupKey,
                        getTotalCount(subGroup.counts),
                      )}
                      title={subGroupName}
                      variant={getSubLabelVariant(selectedSection, subGroupKey)}
                    >
                      {subGroupName}
                    </Typography>
                    <PendingChangesSidebarCounts
                      isSmall
                      activeKind={selectedSection === subGroupKey ? selectedKind : null}
                      counts={subGroup.counts}
                      isRowSelected={selectedSection === subGroupKey}
                      kinds={visibleKinds}
                    />
                  </Box>
                );
              })}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

PendingChangesSidebar.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      counts: countsPropType.isRequired,
      hasSubGroups: PropTypes.bool,
      subGroups: PropTypes.arrayOf(
        PropTypes.shape({
          counts: countsPropType.isRequired,
          name: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  selectedSection: PropTypes.string.isRequired,
  selectedKind: PropTypes.oneOf(Object.values(ChangeKindFilter)).isRequired,
  totalCounts: countsPropType.isRequired,
  onSectionSelect: PropTypes.func.isRequired,
  onKindSelect: PropTypes.func.isRequired,
};

export default memo(PendingChangesSidebar);
